import { useState, useEffect } from 'react';
import { updateSeoAndMetaSetting } from '../../../../services/settingService';

interface SEOSettingsProps {
  settings: any;
  onUpdate: (settings: any) => void;
}

const SEOSettings = ({ settings, onUpdate }: SEOSettingsProps) => {
  const [formData, setFormData] = useState({
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    googleSearchConsole: '',
    canonicalUrl: '',
    robots: 'index, follow'
  });
  const [keywords, setKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (settings) {
      setFormData({
        metaTitle: settings.metaTitle || '',
        metaDescription: settings.metaDescription || '',
        metaKeywords: settings.metaKeywords || '',
        googleSearchConsole: settings.googleSearchConsole || '',
        canonicalUrl: settings.canonicalUrl || '',
        robots: settings.robots || 'index, follow'
      });
      // Parse keywords from comma-separated string to array
      if (settings.metaKeywords) {
        const keywordArray = settings.metaKeywords
          .split(',')
          .map((k: string) => k.trim())
          .filter((k: string) => k.length > 0);
        setKeywords(keywordArray);
      } else {
        setKeywords([]);
      }
    }
  }, [settings]);

  // Update formData.metaKeywords when keywords array changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      metaKeywords: keywords.join(', ')
    }));
  }, [keywords]);

  const handleAddKeyword = () => {
    const trimmedKeyword = newKeyword.trim();
    if (trimmedKeyword && !keywords.includes(trimmedKeyword)) {
      setKeywords([...keywords, trimmedKeyword]);
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(keywords.filter(k => k !== keywordToRemove));
  };

  const handleKeywordInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await updateSeoAndMetaSetting(formData);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Cập nhật thành công' });
        if (onUpdate && response.data) {
          onUpdate(response.data);
        }
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">SEO & Meta Tags</h3>
      
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meta Title
          </label>
          <input
            type="text"
            value={formData.metaTitle}
            onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">Tối đa 60 ký tự</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meta Description
          </label>
          <textarea
            rows={3}
            value={formData.metaDescription}
            onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
          />
          <p className="text-sm text-gray-500 mt-1">Tối đa 160 ký tự</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Keywords
          </label>
          
          {/* Keywords Tags Display */}
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2A4B7C] text-white text-sm font-medium rounded-full"
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Add New Keyword Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={handleKeywordInputKeyDown}
              placeholder="Nhập keyword mới..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
            <button
              type="button"
              onClick={handleAddKeyword}
              disabled={!newKeyword.trim()}
              className="px-4 py-3 bg-[#00C897] hover:bg-[#00C897]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Thêm
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">Nhấn Enter hoặc nút Thêm để thêm keyword mới</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Canonical URL
          </label>
          <input
            type="url"
            placeholder="https://stackway.vn"
            value={formData.canonicalUrl}
            onChange={(e) => setFormData({ ...formData, canonicalUrl: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">URL chuẩn của trang chủ (ví dụ: https://stackway.vn)</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Robots
          </label>
          <input
            type="text"
            placeholder="index, follow"
            value={formData.robots}
            onChange={(e) => setFormData({ ...formData, robots: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">Meta robots (mặc định: index, follow)</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Search Console
          </label>
          <textarea
            rows={3}
            placeholder="Dán mã xác minh Google Search Console tại đây"
            value={formData.googleSearchConsole}
            onChange={(e) => setFormData({ ...formData, googleSearchConsole: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Đang lưu...' : 'Lưu cài đặt SEO'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SEOSettings;

